import React, { useCallback, useMemo } from 'react';
import _, { groupBy, isEqual, property, shuffle, times } from 'lodash';

import SectionDividers from '@dyl/components/SectionDividers';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import RubricBlock from '@dyl/blocks/RubricBlock';
import { Grid, IconButton, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ConnectingLinesRenderer } from '@dyl/components/ConnectingLines';
import { Restore as ResetIcon, Shuffle as ShuffleIcon } from '@mui/icons-material';
import Stimulus from './Stimulus';
import Answer from './Answer';
import TaskRadioTextBlock from "@dyl/blocks/TaskRadioTextBlock";

const ELEMENT_COUNTS = [5, 4, 4];

const useStyles = makeStyles(theme => ({
    root: {
        maxWidth: theme.breakpoints.values.md,
    },
    groupHeader: {
        marginBottom: '16px',
        textAlign: 'center'
    },
}));

export default function Editor() {
    const classes = useStyles();
    const [items] = useTaskField('items');
    const pages = useMemo(() =>
        Object.values(groupBy(items.filter(x => x.phase === 'PHASE_1'), x => x?.page)),
        [items]
    );
    const phase2Items = items.filter(x => x.phase === 'PHASE_2');

    return (
        <div className={classes.root}>
            <SectionDividers>
                <>
                    <Typography variant='h4' className={classes.groupHeader}>Part 1</Typography>
                    <RubricBlock phase={'PHASE_1'} />
                </>
                {pages.map((page, pageIndex) => <Page key={pageIndex} page={page} pageIndex={pageIndex} />)}
                <>
                    <Typography variant='h4' className={classes.groupHeader}>Part 2</Typography>
                    <RubricBlock index={7} phase={'PHASE_2'} />
                </>
                {phase2Items.map(({ index }) => <Phase2Item key={index} index={index} />)}
            </SectionDividers>
        </div>
    );
}

function Page({ pageIndex, page }) {
    const [items, setItems] = useTaskField('items');
    const displayOrders = useMemo(() => _(page)
        .flatMap((item, index) => (
            item?.answers.map((a, i) => ({
                index: item.index,
                answerIndex: i,
                displayOrder: a.display_order,
                isDistractor: !a.is_correct_answer,
            }))))
        .sortBy(property('displayOrder'))
        .value(), [items]);

    const resetOrder = useCallback(() => {
        let index = 1;
        const relevantItemIndices = items.filter(x => x.page === pageIndex + 1).map(x => x.index);
        const itemsNew = items.map((item) => {
            if (!relevantItemIndices.includes(item.index)) {
                item.answers.forEach(() => ++index);
                return item;
            }
            return {
                ...item,
                answers: item.answers.map(answer => ({
                    ...answer,
                    display_order: index++
                })),
            };
        });
        if (!isEqual(items, itemsNew)) setItems(itemsNew);
    }, [items]);

    const base = ELEMENT_COUNTS.slice(0, pageIndex).reduce((x, y) => x + y, 0);
    const shuffleOrder = useCallback(() => {
        const relevantItemIndices = items.filter(x => x.page === pageIndex + 1).map(x => x.index);
        const oldDisplayOrder = page.flatMap(x => x.answers.map(y => y.display_order));

        /* Attempt up to 100 times to get a different order to last time. */
        const displayOrder = (() => {
            let order = [];
            for (let attempt = 0; attempt < 100; attempt++) {
                order = shuffle(times(ELEMENT_COUNTS[pageIndex], i => base + i + 1));
                if (!isEqual(order, oldDisplayOrder)) { break; }
            }
            return order;
        })();

        let index = 0;
        const itemsNew = items.map((item) => {
            if (!relevantItemIndices.includes(item.index)) return item;
            return {
                ...item,
                answers: item.answers.map(answer => ({
                    ...answer,
                    display_order: displayOrder[index++],
                })),
            };
        });
        setItems(itemsNew);
    }, [items]);

    return (
        <>
            <Grid container spacing={2} style={{ paddingBottom: "12px" }}>
                <Grid item xs={12} sm={12}>
                    <Typography variant='h6'>
                        Page {pageIndex + 1}
                    </Typography>
                </Grid>
                <>
                    <Grid item xs={5}>
                        <div style={{ flex: 1 }}>
                            <Typography variant='body1'>
                                Stimuli
                            </Typography>
                            <Typography variant='caption'>
                                Add an underscore in your stimuli to indicate where the gap is.
                            </Typography>
                        </div>
                    </Grid>
                    <Grid item xs={1} />
                    <Grid item xs={6} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                            <Typography variant='body1'>
                                Answers
                            </Typography>
                            <Typography variant='caption'>
                                Drag the handles to change display order.
                                <br />
                                Type to add and delete answers and distractors.
                            </Typography>
                        </div>

                        {/* Buttons */}
                        <Tooltip title='Reset Order'>
                            <IconButton onClick={resetOrder} tabIndex={-1} size="large">
                                <ResetIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title='Shuffle Order'>
                            <IconButton onClick={shuffleOrder} tabIndex={-1} size="large">
                                <ShuffleIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </>
            </Grid>

            <ConnectingLinesRenderer>
                <Grid container spacing={2}>
                    <>
                        {/* Stimulus column */}
                        <Grid item xs={5}>
                            {page.map(({ index }) => {
                                return <Stimulus key={index} index={index} />;
                            })}
                        </Grid>
                        <Grid item xs={1} />
                        <Grid item xs={6}>
                            {displayOrders.map(({ index, answerIndex, displayOrder, isDistractor }) => {
                                return <Answer
                                    key={20 * index + answerIndex}
                                    ELEMENT_COUNT={ELEMENT_COUNTS[pageIndex]}
                                    index={index}
                                    answerIndex={answerIndex}
                                    isDistractor={isDistractor}
                                    displayOrder={displayOrder}
                                    list={displayOrders}
                                    pageIndex={pageIndex}
                                />;
                            })}
                        </Grid>
                    </>
                </Grid>
            </ConnectingLinesRenderer>
        </>
    )
}

function Phase2Item({ index }) {
    return (
        <TaskRadioTextBlock path={['items', index, 'answers']} />
    )
}